// apps/web/src/features/agent/AgentComposer.tsx
//
// Question composer for the analyst agent. React Hook Form +
// `zodResolver(AgentV2AskRequestSchema)` validates the question (trimmed,
// 1..4000) before calling `onSubmit`. v2 has no preset period field: the agent
// derives or clarifies time ranges through its tool loop.
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { AgentV2AskRequestSchema, type AgentV2AskRequest } from '@magimp/shared';
import { Button, FormRow, Textarea } from '../../ui/index.js';

export interface AgentComposerProps {
  disabled: boolean;
  onSubmit: (question: string) => void;
}

// The form value is the wire shape itself; `conversationId` is owned by the
// hook (not user-entered) so it is never part of the form.
type ComposerValues = Omit<AgentV2AskRequest, 'conversationId'>;

export function AgentComposer({ disabled, onSubmit }: AgentComposerProps) {
  const { t } = useTranslation('agent');

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ComposerValues>({
    resolver: zodResolver(AgentV2AskRequestSchema),
    defaultValues: { question: '' },
  });

  const submit = handleSubmit((values) => {
    onSubmit(values.question);
    reset({ question: '' });
  });

  // Localize the question validation error: map the Zod issue code (RHF's
  // error `type`) to an i18n key so ru/zh-Hans never leak the English default.
  const questionError: string | undefined = errors.question
    ? errors.question.type === 'too_big'
      ? t('composer.errorTooLong')
      : t('composer.errorRequired')
    : undefined;

  return (
    <form
      onSubmit={submit}
      data-testid="agent-composer"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-2)' }}
    >
      <Controller
        name="question"
        control={control}
        render={({ field }) => (
          <FormRow label={t('composer.questionLabel')} error={questionError}>
            <Textarea
              value={field.value}
              onChange={field.onChange}
              ariaLabel={t('composer.questionLabel')}
              placeholder={t('composer.placeholder')}
              rows={3}
              readOnly={disabled}
              onKeyDown={(e) => {
                // Enter submits; Shift+Enter inserts a newline.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (!disabled) void submit();
                }
              }}
            />
          </FormRow>
        )}
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          kind="primary"
          icon="send-horizontal"
          disabled={disabled}
          data-testid="agent-composer-send"
          style={{ marginBottom: 10 }}
        >
          {t('composer.send')}
        </Button>
      </div>
    </form>
  );
}
